import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faDumbbell,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

export const analyticsModalBase = {
  text: "#0A1D44",
  muted: "#64748B",
  border: "#E0E7EF",
};

export const analyticsGold = "#D5A419";

export function WorkoutGroupAnalyticsModalFrame({
  title = "Group Analytics",
  subtitle = "",
  onClose,
  children,
  zIndex = 2050,
}) {
  return (
    <div
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      className="modal-wrap"
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        background: "rgba(10,29,68,0.35)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="workout-group-analytics-shared-title"
        onClick={(event) => event.stopPropagation()}
        className="modal-animate modal-inner club-analytics-modal-panel"
        style={{
          width: "min(1040px,100%)",
          maxHeight: "90svh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 20,
          background: "#FFFFFF",
          border: `1px solid ${analyticsModalBase.border}`,
          boxShadow: "0 24px 70px rgba(10,29,68,0.30)",
        }}
      >
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${analyticsModalBase.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div id="workout-group-analytics-shared-title" className="heading" style={{ fontSize: 16, fontWeight: 700, color: analyticsModalBase.text }}>{title}</div>
            {subtitle ? (
              <div style={{ color: analyticsModalBase.muted, fontSize: 12, fontWeight: 850, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close group analytics"
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: `1px solid ${analyticsModalBase.border}`,
              background: "transparent",
              color: analyticsModalBase.muted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
          </button>
        </div>
        <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gap: 12 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export const WorkoutGroupAnalyticsIcon = ({ icon, color = "#0C69C8", bg = "#E6F0FF", size = 38 }) => (
  <span
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      borderRadius: Math.max(10, Math.round(size * 0.36)),
      background: bg,
      color,
      display: "inline-grid",
      placeItems: "center",
      flexShrink: 0,
    }}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: Math.max(11, Math.round(size * 0.38)) }} />
  </span>
);

export const WorkoutGroupAnalyticsMetricCard = ({ icon, label, value, color = "#0C69C8", bg = "#E6F0FF", wide = false }) => (
  <div style={{ position: "relative", minHeight: wide ? 132 : 122, border: `1px solid ${analyticsModalBase.border}`, background: "#FFFFFF", borderRadius: 20, padding: 14, overflow: "hidden", display: "grid", alignContent: "space-between" }}>
    <span aria-hidden="true" style={{ position: "absolute", right: -20, top: -24, width: 116, height: 88, borderRadius: 34, background: color, opacity: 0.08, transform: "rotate(-18deg)" }} />
    {icon && <FontAwesomeIcon aria-hidden="true" icon={icon} style={{ position: "absolute", right: -12, bottom: -14, color, opacity: 0.055, fontSize: 58 }} />}
    <WorkoutGroupAnalyticsIcon icon={icon} color={color} bg={bg} size={42} />
    <div style={{ position: "relative" }}>
      <div style={{ color: "#0D275B", fontSize: 28, lineHeight: 1, fontWeight: 950 }}>{value}</div>
      <div style={{ color: analyticsModalBase.muted, fontSize: 12, lineHeight: 1.2, fontWeight: 950, textTransform: "uppercase", marginTop: 7 }}>{label}</div>
    </div>
  </div>
);

export const WorkoutGroupNutritionTile = ({
  icon,
  label,
  value,
  sub,
  color = analyticsGold,
  bg = "#FFF8E1",
  emphasis = false,
}) => (
  <div
    style={{
      position: "relative",
      minHeight: emphasis ? 132 : 118,
      border: `1px solid ${emphasis ? "#FDE68A" : analyticsModalBase.border}`,
      background: emphasis ? "#FFFBEB" : "#FFFFFF",
      borderRadius: 18,
      padding: 13,
      overflow: "hidden",
      display: "grid",
      alignContent: "space-between",
      gap: 10,
    }}
  >
    <span aria-hidden="true" style={{ position: "absolute", right: -24, top: -24, width: 96, height: 72, borderRadius: 26, background: color, opacity: emphasis ? 0.13 : 0.08, transform: "rotate(-18deg)" }} />
    <WorkoutGroupAnalyticsIcon icon={icon} color={color} bg={bg} size={38} />
    <div style={{ position: "relative", minWidth: 0 }}>
      <div style={{ color: analyticsModalBase.text, fontSize: emphasis ? 24 : 21, lineHeight: 1.05, fontWeight: 950, overflowWrap: "anywhere" }}>{value}</div>
      <div style={{ color: analyticsModalBase.muted, fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 6 }}>{label}</div>
      {sub ? <div style={{ color: analyticsModalBase.muted, fontSize: 11, fontWeight: 800, lineHeight: 1.35, marginTop: 5 }}>{sub}</div> : null}
    </div>
  </div>
);

export function WorkoutGroupAnalyticsTeamStatusCard({
  selectedAthlete,
  selectedAthleteLabel = "",
  sessionTarget = 0,
  onPlanCount = 0,
  athleteCount = 0,
  activityCount = 0,
  closeCount = 0,
  completedSessions = 0,
  sessionCompletion = 0,
  primary = "#0C69C8",
  secondary = "#0D275B",
  onRemind,
  onReviewAthletes,
}) {
  return (
    <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${primary}`, background: `linear-gradient(135deg, ${primary}, ${secondary})`, borderRadius: 22, padding: 18, color: "#fff" }}>
      <span aria-hidden="true" style={{ position: "absolute", right: -24, top: 14, width: 160, height: 88, borderRadius: 32, background: "rgba(255,255,255,0.10)", transform: "rotate(-16deg)" }} />
      <FontAwesomeIcon aria-hidden="true" icon={faChartLine} style={{ position: "absolute", right: 28, bottom: 16, color: "#FFFFFF", opacity: 0.14, fontSize: 76 }} />
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 210px", gap: 16, alignItems: "center" }}>
        <div>
          <div style={{ color: "rgba(255,255,255,0.76)", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>{selectedAthlete ? "Athlete status" : "Team status"}</div>
          <h3 style={{ color: "#fff", margin: "7px 0 0", fontSize: 31, lineHeight: 1.1, fontWeight: 950 }}>
            {selectedAthlete ? `${selectedAthlete.sessions}/${sessionTarget} sessions` : `${onPlanCount}/${athleteCount} on plan`}
          </h3>
          <div style={{ color: "rgba(255,255,255,0.76)", fontSize: 13, fontWeight: 850, lineHeight: 1.45, marginTop: 8 }}>
            {selectedAthlete
              ? `${Number(selectedAthlete.xp || 0).toLocaleString()} XP · ${Number(selectedAthlete.calories || 0).toLocaleString()} kcal · ${selectedAthleteLabel}`
              : `${activityCount} need activity · ${closeCount} close · ${Number(completedSessions || 0).toLocaleString()} completed sessions`}
          </div>
          {!selectedAthlete && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              <button type="button" onClick={onRemind} style={{ minHeight: 38, border: "1px solid rgba(255,255,255,0.28)", background: "#fff", color: primary, borderRadius: 12, padding: "8px 12px", fontSize: 12, fontWeight: 950, cursor: "pointer" }}>Remind athletes</button>
              <button type="button" onClick={onReviewAthletes} style={{ minHeight: 38, border: "1px solid rgba(255,255,255,0.24)", background: "rgba(255,255,255,0.10)", color: "#fff", borderRadius: 12, padding: "8px 12px", fontSize: 12, fontWeight: 950, cursor: "pointer" }}>Review athletes</button>
            </div>
          )}
        </div>
        <div style={{ border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.10)", borderRadius: 20, padding: 14, display: "grid", gap: 10 }}>
          {[
            ["On plan", `${onPlanCount}/${athleteCount}`],
            ["Close", closeCount.toLocaleString()],
            ["Needs activity", activityCount.toLocaleString()],
            ["Completion", `${sessionCompletion}%`],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <span style={{ color: "rgba(255,255,255,0.72)", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>{label}</span>
              <span style={{ color: "#fff", fontSize: 15, fontWeight: 950 }}>{value}</span>
            </div>
          ))}
          <div style={{ height: 9, background: "rgba(255,255,255,0.20)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, sessionCompletion))}%`, background: "#FFFFFF", borderRadius: 999 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function WorkoutGroupExerciseHeaderIcon({ icon = faDumbbell, color = "#0C69C8" }) {
  return <WorkoutGroupAnalyticsIcon icon={icon} color={color} bg="#E6F0FF" size={38} />;
}

export function WorkoutGroupAnalyticsAthleteSelector({
  athletes = [],
  selectedId = "all",
  onSelectAll,
  onSelectAthlete,
  accent = "#0C69C8",
  noImage,
}) {
  return (
    <section style={{ background: "#fff", border: `1px solid ${analyticsModalBase.border}`, borderRadius: 20, padding: 14 }}>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        <button
          type="button"
          aria-pressed={selectedId === "all"}
          onClick={onSelectAll}
          style={{
            border: `1px solid ${selectedId === "all" ? accent : analyticsModalBase.border}`,
            background: selectedId === "all" ? accent : "#F8FAFF",
            color: selectedId === "all" ? "#fff" : analyticsModalBase.text,
            borderRadius: 999,
            padding: "8px 11px",
            fontSize: 12,
            fontWeight: 950,
            cursor: "pointer",
          }}
        >
          All athletes
        </button>
        {athletes.map((athlete) => {
          const selected = selectedId === athlete.id;
          const name = athlete.name || [athlete.first_name, athlete.surname].filter(Boolean).join(" ") || athlete.username || "Athlete";
          return (
            <button
              key={athlete.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectAthlete?.(athlete)}
              style={{
                border: `1px solid ${selected ? accent : analyticsModalBase.border}`,
                background: selected ? "#EAF3FF" : "#fff",
                color: selected ? accent : analyticsModalBase.text,
                borderRadius: 999,
                padding: "7px 10px",
                fontSize: 12,
                fontWeight: 950,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                maxWidth: 220,
              }}
            >
              <img
                src={athlete.profile_img || noImage}
                alt=""
                style={{ width: 22, height: 22, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                loading="lazy"
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
